import {createContext, useContext, useState} from 'react'
import axios from 'axios'
import {useQueryClient, useMutation, useQuery} from 'react-query'
import {useAuth} from '../../modules/auth'

const CourseContext = createContext()

const BASE_URL = process.env.REACT_APP_BASE_URL

// Helper: get selectedCompany from localStorage.
// Uses tenant-scoped key to prevent cross-tenant contamination.
const getSelectedCompanyId = () => {
  try {
    // Try tenant-scoped key first, fall back to legacy key
    const authRaw = localStorage.getItem('kt-auth-react-v')
    const tenantId = authRaw ? (JSON.parse(authRaw)?.tenantId || '') : ''
    const scopedKey = tenantId ? `selectedCompany_${tenantId}` : 'selectedCompany'
    const saved = localStorage.getItem(scopedKey) || localStorage.getItem('selectedCompany')
    if (saved) return JSON.parse(saved)?._id || ''
  } catch(e) {}
  return ''
}

export const CourseContextProvider = ({children}) => {
  const queryClient = useQueryClient()
  const {auth, currentUser} = useAuth()
  let config = {
    headers: {
      Authorization: `Bearer ${auth?.api_token}`,
    },
  }

  const getCourseLists = useQuery({
    queryKey: ['getCourseLists', getSelectedCompanyId()],
    queryFn: async () => {
      try {
        const cId = getSelectedCompanyId()
        const url = cId ? `${BASE_URL}/api/courses?companyId=${cId}` : `${BASE_URL}/api/courses`
        const response = await axios.get(url, config)
        return response.data
      } catch (error) {
        throw new Error('Error fetching course data: ' + error.message)
      }
    },
  })
  const useGetSingleStudentCourse = (courseId) => {
    return useQuery({
      queryKey: ['getCourseLists', courseId],
      queryFn: async () => {
        try {
          const response = await axios.get(`${BASE_URL}/api/courses/${courseId}`, config)
          return response.data
        } catch (error) {
          throw new Error('Error fetching student data: ' + error.message)
        }
      },
    })
  }

  //console.log(getCourseLists)

  //console.log(studentsLists)
  const createCourseMutation = useMutation({
    mutationFn: async (data) => {
      // Inject companyId from selected company so course is scoped to the active company
      const cId = getSelectedCompanyId()
      const payload = typeof data === 'object' && !(data instanceof FormData)
        ? {...data, companyId: cId || null}
        : data
      return axios
        .post(`${BASE_URL}/api/courses`, payload, config)
        .then((res) => localStorage.setItem('AddedNewCourse', JSON.stringify(res.data)))
    },
    onMutate: () => {
      //console.log('mutate')
    },

    onError: () => {
      //console.log('error')
    },

    onSuccess: () => {
      alert('Added Course  Successfully!')
      //console.log('success')
    },

    onSettled: async (_, error) => {
      //console.log('settled')
      if (error) {
        //console.log(error)
        alert(error.response.data.error)
      } else {
        await queryClient.invalidateQueries({queryKey: ['getCourseLists']})
      }
    },
  })

  // Course Types
  const deleteCourseMutation = useMutation({
    mutationFn: async (id) => {
      return axios.delete(`${BASE_URL}/api/courses/${id}`, config).then((res) => res.data)
    },
    onSuccess: () => {
      alert('Course  deleted successfully')
    },
    onSettled: async (_, error) => {
      if (error) {
        alert(error)
      } else {
        await queryClient.invalidateQueries({queryKey: ['getCourseLists']})
      }
    },
  })

  // update Course type
  const updateCourseMutation = useMutation({
    mutationFn: async (updateData) => {
      return axios
        .put(`${BASE_URL}/api/courses/${updateData._id}`, updateData, config)
        .then((res) => res.data)
    },
    onSettled: async (_, error) => {
      if (!error) {
        await queryClient.invalidateQueries({
          queryKey: ['getCourseLists'],
        })
        await queryClient.invalidateQueries({
          queryKey: ['getCourseSubjectLists'],
        })
      }
    },
  })

  return (
    <CourseContext.Provider
      value={{
        getCourseLists,
        deleteCourseMutation,
        createCourseMutation,
        updateCourseMutation,
        useGetSingleStudentCourse,
      }}
    >
      {children}
    </CourseContext.Provider>
  )
}

export const useCourseContext = () => {
  const context = useContext(CourseContext)
  if (!context) {
    throw new Error('useAdmissionContext must be used within an AdmissionContextProvider')
  }
  return context
}
